// U4 "A draft on every reply": what a card that needs a reply has to show. Every such card reaches
// the owner with a draft, a line saying drafting is paused to save spend, or a one-line reason with
// one question (a `draft_question` block) whose answers each draft the reply. Pure and zod-free, so
// the server and the web bundle read cards the same way.
import type { CardAsk } from "./startEasily";

interface BlockLike {
  id: string;
  type: string;
  value?: string | undefined;
  text?: string | undefined;
  question?: string | undefined;
  choices?: readonly string[] | undefined;
}

interface CardLike {
  kind?: string;
  ask?: CardAsk | null;
  blocks: readonly BlockLike[];
}

/** The answer the app always adds to a draft question: it opens the instruction input. */
export const TELL_ME = "Tell me";

/** How a draft written ahead ends when the spend cap is reached (the card says so). */
export const PAUSED_FOR_SPEND = "Paused to save spend.";

/** True when the judge said the thread needs a reply drafted (U3's `ask.needs`). */
export function needsReply(card: Pick<CardLike, "kind" | "ask">): boolean {
  if (card.kind && card.kind !== "attention") return false;
  return card.ask?.needs?.draft === true;
}

/** The card's written draft: an editable_text block with text in it. */
export function draftOf<B extends BlockLike>(blocks: readonly B[]): B | null {
  return (
    blocks.find((b) => b.type === "editable_text" && !!(b.value ?? b.text ?? "").trim()) ?? null
  );
}

/** The card's draft question, when it is well formed (a question and at least one answer). */
export function questionOf<B extends BlockLike>(blocks: readonly B[]): B | null {
  return (
    blocks.find(
      (b) => b.type === "draft_question" && !!b.question?.trim() && (b.choices?.length ?? 0) > 0,
    ) ?? null
  );
}

/** How a card that needs a reply stands: a draft, a question, or neither yet. */
export type DraftCoverage = "draft" | "question" | "none" | "not_needed";

export function draftCoverage(card: CardLike): DraftCoverage {
  if (draftOf(card.blocks)) return "draft";
  if (!needsReply(card)) return "not_needed";
  return questionOf(card.blocks) ? "question" : "none";
}

/** The instruction an answer to the card's question carries to the operator (owner's words). */
export function answerInstruction(question: string | null, answer: string): string {
  const q = question?.trim();
  const a = answer.trim();
  return q
    ? `The owner answered your question "${q}": ${a}. Write the reply draft that follows from this answer.`
    : `The owner says: ${a}. Write the reply draft that follows from this.`;
}
